
import type { DataKind } from "./schemaSpec.ts";

export type ParamKind = "stat" | "currency" | "item" | "scope" | null;

export type EntityTypeName =
	| "ItemInstance"
	| "ItemGroup"
	| "Recipe"
	| "Quest"
	| "Item"
	| "Stat"
	| "Currency"
	| "Attack"
	| "Number"

	| "ServerPlayer"
	| "PlayerTeam"
	| "TeamMember"
	| "TeamInvite"
	| "MatchTeam"
	| "DamageContribution"
	| "ChoiceCase"
	| "ActiveTimer"
	| "QuestProgress"
	| "QuestObjectiveProgress"
	| "QuestItemTarget";

export type GetOutput = {
	name: string;
	kind: DataKind;
};

export type GetSourceSpec = {

	source: string;

	isList: boolean;

	paramKind: ParamKind;

	elementType: EntityTypeName | null;

	outputs: readonly GetOutput[];

	dynamicOutputsFromContextRegistry?: boolean;

	label: { en: string; fr: string };
};

const OUT_LIST: readonly GetOutput[] = [
	{ name: "items", kind: "list" },
	{ name: "count", kind: "number" },
];

const GET_REGISTRY_DEFINITIONS = [

	{
		source: "System.Now",
		isList: false,
		paramKind: null,
		elementType: "Number",
		outputs: [{ name: "value", kind: "number" }],
		label: { en: "System → Now (epoch s)", fr: "Système → Maintenant (s)" },
	},
	{
		source: "Player.Stats",
		isList: true,
		paramKind: null,
		elementType: "Stat",
		outputs: OUT_LIST,
		label: { en: "Player → Stats", fr: "Joueur → Stats" },
	},
	{
		source: "Player.Currencies",
		isList: true,
		paramKind: null,
		elementType: "Currency",
		outputs: OUT_LIST,
		label: { en: "Player → Currencies", fr: "Joueur → Monnaies" },
	},
	{
		source: "Player.Items",
		isList: true,
		paramKind: null,
		elementType: "ItemGroup",
		outputs: OUT_LIST,
		label: { en: "Player → Items (grouped by itemId)", fr: "Joueur → Objets (groupés par itemId)" },
	},

	{
		source: "Player.ItemInstances",
		isList: true,
		paramKind: null,
		elementType: "ItemInstance",
		outputs: OUT_LIST,
		label: { en: "Player - Item instances", fr: "Joueur - Instances d'objets" },
	},
	{
		source: "Player.Quests",
		isList: true,
		paramKind: null,
		elementType: "QuestProgress",
		outputs: OUT_LIST,
		label: { en: "Player - Quests", fr: "Joueur - Quêtes" },
	},
	{
		source: "Server.Players",
		isList: true,
		paramKind: null,
		elementType: "ServerPlayer",
		outputs: OUT_LIST,
		label: { en: "Server - Players", fr: "Serveur - Joueurs" },
	},
	{
		source: "Player.Team",
		isList: false,
		paramKind: null,
		elementType: "PlayerTeam",
		outputs: [
			{ name: "hasTeam", kind: "boolean" },
			{ name: "isLeader", kind: "boolean" },
			{ name: "leaderUserId", kind: "number" },
			{ name: "memberCount", kind: "number" },
			{ name: "maxSize", kind: "number" },
		],
		label: { en: "Player - Team state", fr: "Joueur - État d'équipe" },
	},
	{
		source: "Player.TeamMembers",
		isList: true,
		paramKind: null,
		elementType: "TeamMember",
		outputs: OUT_LIST,
		label: { en: "Player - Team members", fr: "Joueur - Membres d'équipe" },
	},
	{
		source: "Player.TeamInvites",
		isList: true,
		paramKind: null,
		elementType: "TeamInvite",
		outputs: OUT_LIST,
		label: { en: "Player - Team invitations", fr: "Joueur - Invitations d'équipe" },
	},
	{
		source: "Session.Teams",
		isList: true,
		paramKind: null,
		elementType: "MatchTeam",
		outputs: OUT_LIST,
		label: { en: "Session - Teams", fr: "Session - Équipes" },
	},

	{
		source: "Project.Recipes",
		isList: true,
		paramKind: null,
		elementType: "Recipe",
		outputs: OUT_LIST,
		label: { en: "Project → Recipes", fr: "Projet → Recettes" },
	},
	{
		source: "Project.Quests",
		isList: true,
		paramKind: null,
		elementType: "Quest",
		outputs: OUT_LIST,
		label: { en: "Project → Quests", fr: "Projet → Quêtes" },
	},
	{
		source: "Project.Items",
		isList: true,
		paramKind: null,
		elementType: "Item",
		outputs: OUT_LIST,
		label: { en: "Project → Items (templates)", fr: "Projet → Objets (modèles)" },
	},
	{
		source: "Project.Stats",
		isList: true,
		paramKind: null,
		elementType: "Stat",
		outputs: OUT_LIST,
		label: { en: "Project → Stats", fr: "Projet → Stats" },
	},
	{
		source: "Project.Currencies",
		isList: true,
		paramKind: null,
		elementType: "Currency",
		outputs: OUT_LIST,
		label: { en: "Project → Currencies", fr: "Projet → Monnaies" },
	},
	{
		source: "Project.Attacks",
		isList: true,
		paramKind: null,
		elementType: "Attack",
		outputs: OUT_LIST,
		label: { en: "Project → Attacks", fr: "Projet → Attaques" },
	},

	{
		source: "Context",
		isList: false,
		paramKind: "scope",
		elementType: null,
		outputs: [],
		dynamicOutputsFromContextRegistry: true,
		label: { en: "Context (event / UI role)", fr: "Contexte (événement / rôle UI)" },
	},

	{
		source: "State",
		isList: false,
		paramKind: "scope",
		elementType: null,
		outputs: [
			{ name: "value", kind: "any" },
			{ name: "items", kind: "list" },
			{ name: "count", kind: "number" },
		],
		label: { en: "UI state (this window)", fr: "État UI (cette fenêtre)" },
	},
] as const satisfies readonly GetSourceSpec[];

export type GetSource = (typeof GET_REGISTRY_DEFINITIONS)[number]["source"];

export const GET_REGISTRY: readonly GetSourceSpec[] = GET_REGISTRY_DEFINITIONS;

const GET_REGISTRY_BY_SOURCE = Object.fromEntries(
	GET_REGISTRY_DEFINITIONS.map((s) => [s.source, s]),
) as Record<GetSource, GetSourceSpec>;

export const GET_SOURCES: readonly GetSource[] = GET_REGISTRY_DEFINITIONS.map(
	(s) => s.source,
);

export const getSourceSpec = (source: string): GetSourceSpec | undefined =>
	GET_REGISTRY_BY_SOURCE[source as GetSource];

export const isValidGetOutput = (source: string, output: string): boolean => {
	const spec = getSourceSpec(source);
	if (!spec) return false;
	return spec.outputs.some((o) => o.name === output);
};
